import { Title } from '@solidjs/meta'
import type { JSX } from 'solid-js'
import { createSignal, For } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import type { ButtonColorType, ButtonSizeType, ButtonVariantType } from '~/types/Button'

const buttonColors: ButtonColorType[] = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'default']
const buttonVariants: ButtonVariantType[] = ['contained', 'outlined', 'soft', 'text']
const buttonSizes: Record<ButtonSizeType, string> = {
  xs: 'Extra Small',
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
  xl: 'Extra Large'
}

export default function ButtonPage(): JSX.Element {
  const [loading, setLoading] = createSignal(false)

  const simulateLoading = () => {
    setLoading(true)
    setTimeout(() => setLoading(false), 2000)
  }

  return (
    <>
      <Title>Button Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">Button</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            El componente fundamental para la interacción. Permite a los usuarios realizar acciones, enviar formularios y navegar con una respuesta visual clara y profesional.
          </p>
        </div>

        <div class="space-y-12">
          {/* Variantes y Colores */}
          <Card>
            <Card.Header
              title="Variantes y Paleta Cromática"
              subtitle="Cuatro estilos visuales y siete colores semánticos para cubrir cualquier necesidad de diseño."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10">
                <For each={buttonVariants}>
                  {variant => (
                    <div class="space-y-4">
                      <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest px-1 border-l-2 border-primary-500/30 ml-1">Variante {variant}</h4>
                      <div class="flex flex-wrap items-center gap-4 p-4 bg-surface-muted rounded-xl border border-border">
                        <For each={buttonColors}>
                          {color => (
                            <Button color={color} variant={variant}>
                              {color}
                            </Button>
                          )}
                        </For>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Dimensiones"
              subtitle="Desde botones compactos para barras de herramientas hasta botones grandes para llamadas a la acción."
              icon={<Icon icon="material-symbols-light:maximize" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-wrap items-end justify-center gap-8 p-10 bg-surface-muted rounded-xl border border-border shadow-inner">
                <For each={Object.entries(buttonSizes)}>
                  {([size, label]) => (
                    <div class="flex flex-col items-center gap-3">
                      <Button size={size as ButtonSizeType} color="primary">
                        {size.toUpperCase()}
                      </Button>
                      <span class="text-[10px] font-bold text-foreground-subtle uppercase">{label}</span>
                    </div>
                  )}
                </For>
              </div>
            </Card.Body>
          </Card>

          {/* Iconos */}
          <Card>
            <Card.Header
              title="Composición con Iconos"
              subtitle="Añade contexto visual utilizando iconos al inicio, al final o como elemento único."
              icon={<Icon icon="material-symbols-light:star-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-wrap items-center justify-center gap-6 p-10 bg-surface-muted rounded-xl border border-border">
                <Button color="primary" startIcon="material-symbols-light:add-circle-outline">
                  Nuevo Proyecto
                </Button>
                <Button color="secondary" variant="outlined" endIcon="material-symbols-light:arrow-upward">
                  Ver Detalles
                </Button>
                <Button color="success" variant="soft" startIcon="material-symbols-light:save-outline">
                  Guardar
                </Button>
                <div class="w-px h-8 bg-surface-muted mx-2" />
                <Button color="primary" icon="material-symbols-light:search" />
                <Button color="danger" variant="outlined" icon="material-symbols-light:delete-outline" />
                <Button color="warning" variant="text" icon="material-symbols-light:notifications-outline" />
                <Button color="info" icon="material-symbols-light:share-outline" pill />
                <Button color="secondary" variant="soft" icon="material-symbols-light:settings-outline" />
              </div>
            </Card.Body>
          </Card>

          {/* Formas y Estilos Especiales — NEW SECTION */}
          <Card>
            <Card.Header
              title="Geometría y Estilos Fantasma"
              subtitle="Control total sobre la curvatura y estados de transparencia para una UI minimalista."
              icon={<Icon icon="material-symbols-light:masks-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="p-8 bg-surface-muted rounded-xl border border-border space-y-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest px-2 border-l-2 border-emerald-500">Pill vs Rounded</h4>
                  <div class="flex flex-wrap gap-4 items-center justify-center pt-4">
                    <Button color="primary" pill>
                      Botón Píldora
                    </Button>
                    <Button color="primary" pill variant="outlined">
                      Outlined Pill
                    </Button>
                    <Button color="secondary" class="rounded-none">
                      Sin Redondeo
                    </Button>
                    <Button color="success" class="rounded-3xl">
                      Extra Round
                    </Button>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border space-y-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest px-2 border-l-2 border-primary-500">Ghost Mode (Text)</h4>
                  <div class="flex flex-wrap gap-4 items-center justify-center pt-4">
                    <Button variant="text" color="primary">
                      Click aquí
                    </Button>
                    <Button variant="text" color="secondary" startIcon="material-symbols-light:mood-outline">
                      Amigable
                    </Button>
                    <Button variant="text" color="danger">
                      Eliminar
                    </Button>
                    <Button variant="text" color="default" class="font-black">
                      NEGRITA
                    </Button>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Estados */}
          <Card>
            <Card.Header
              title="Estados de Interacción"
              subtitle="Manejo de estados deshabilitados y de carga para una retroalimentación precisa."
              icon={<Icon icon="material-symbols-light:mouse-outline" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="p-8 bg-surface-muted rounded-xl border border-border space-y-6">
                  <h4 class="font-bold text-foreground border-b border-border pb-2">Deshabilitado</h4>
                  <div class="flex flex-wrap gap-4">
                    <Button color="primary" disabled>
                      Principal
                    </Button>
                    <Button color="secondary" variant="outlined" disabled>
                      Borde
                    </Button>
                    <Button color="success" variant="soft" disabled>
                      Suave
                    </Button>
                  </div>
                </div>

                <div class="p-8 bg-surface-muted rounded-xl border border-border space-y-6">
                  <h4 class="font-bold text-foreground border-b border-border pb-2">Carga (Loading)</h4>
                  <div class="flex flex-wrap gap-4 items-center">
                    <Button color="primary" loading={loading()} onClick={simulateLoading}>
                      Haz clic para cargar
                    </Button>
                    <Button color="secondary" variant="outlined" loading>
                      Cargando
                    </Button>
                    <Button color="info" icon="material-symbols-light:refresh" loading />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Patrones Compuestos */}
          <Card>
            <Card.Header
              title="Patrones Compuestos"
              subtitle="Combina iconos, badges y acciones secundarias en botones de uso común."
              icon={<Icon icon="material-symbols-light:widgets-outline" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-wrap items-center justify-center gap-6 p-10 bg-surface-muted rounded-xl border border-border">
                <Button color="success" startIcon="material-symbols-light:credit-card-outline">
                  Pay <span class="font-bold mx-0.5">$49</span>
                </Button>
                <Button variant="outlined" startIcon="material-symbols-light:bookmark-outline">
                  Save
                  <span class="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-surface-muted rounded">1337</span>
                </Button>
                <Button variant="outlined" endIcon="material-symbols-light:keyboard-arrow-down">
                  Download
                </Button>
                <Button variant="outlined" startIcon="material-symbols-light:cloud-outline">
                  Invoice
                </Button>
                <Button variant="outlined" startIcon="material-symbols-light:notifications-outline" endIcon="material-symbols-light:keyboard-arrow-down">
                  Subscribe
                </Button>
                <Button color="danger" startIcon="material-symbols-light:play-arrow-outline" endIcon="material-symbols-light:keyboard-arrow-down">
                  Run
                </Button>
                <Button variant="soft" startIcon="material-symbols-light:filter-alt-outline">
                  Filter
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Botones con Entradas */}
          <Card>
            <Card.Header
              title="Botones con Entradas"
              subtitle="Botones combinados con campos de entrada para formularios y barras de búsqueda."
              icon={<Icon icon="material-symbols-light:text-fields" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8">
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-amber-500">Barra de búsqueda</h4>
                  <Button.Group variant="outlined" color="default">
                    <div class="flex items-center flex-1 bg-surface px-3 gap-2">
                      <Icon icon="material-symbols-light:search" size={18} class="text-foreground-subtle" />
                      <span class="text-foreground-subtle text-sm whitespace-nowrap">Search projects...</span>
                    </div>
                    <Button color="primary" class="px-6">
                      Search
                    </Button>
                  </Button.Group>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-amber-500">URL con prefijo</h4>
                  <Button.Group variant="outlined" color="default">
                    <div class="flex items-center bg-surface px-3">
                      <span class="text-foreground-subtle text-sm whitespace-nowrap">https://</span>
                    </div>
                    <div class="flex items-center flex-1 bg-surface px-3">
                      <span class="text-sm">example.com</span>
                    </div>
                  </Button.Group>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-amber-500">Campo con acción</h4>
                  <Button.Group variant="outlined" color="default">
                    <div class="flex items-center flex-1 bg-surface px-3 gap-2">
                      <Icon icon="material-symbols-light:pin-drop-outline" size={18} class="text-foreground-subtle" />
                      <span class="text-foreground-subtle text-sm whitespace-nowrap">Search location...</span>
                    </div>
                    <Button color="success" class="px-5">
                      Go
                    </Button>
                  </Button.Group>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-amber-500">Pegar enlace</h4>
                  <Button.Group variant="outlined" color="default">
                    <div class="flex items-center flex-1 bg-surface px-3">
                      <span class="text-foreground-subtle text-sm whitespace-nowrap">Paste link...</span>
                    </div>
                    <Button icon="material-symbols-light:link" class="rounded-none" />
                    <Button icon="material-symbols-light:check" color="success" class="rounded-none" />
                  </Button.Group>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Controles Segmentados */}
          <Card>
            <Card.Header
              title="Controles Segmentados"
              subtitle="Botones agrupados para alternar entre estados o vistas exclusivos."
              icon={<Icon icon="material-symbols-light:toggle-on-outline" class="text-success-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8">
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-success-500">Selector de tiempo</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" color="default">
                      <Button>Years</Button>
                      <Button>Months</Button>
                      <Button>Days</Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-success-500">Vista de datos</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:grid-view" />
                      <Button icon="material-symbols-light:view-agenda-outline" />
                      <Button icon="material-symbols-light:view-list-outline" />
                    </Button.Group>
                    <Button.Group variant="outlined" color="success">
                      <Button startIcon="material-symbols-light:grid-view" class="px-5">
                        Grid
                      </Button>
                      <Button icon="material-symbols-light:view-list-outline" class="px-3" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-success-500">Modo de tema</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" color="default">
                      <Button startIcon="material-symbols-light:light-mode-outline">Light</Button>
                      <Button startIcon="material-symbols-light:dark-mode-outline">Dark</Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-success-500">Estados activos</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" color="default">
                      <Button>Active</Button>
                      <Button disabled>Disabled</Button>
                      <Button>Active</Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-success-500">Alineación de texto</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:format-align-left" />
                      <Button icon="material-symbols-light:format-align-center" />
                      <Button icon="material-symbols-light:format-align-right" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-success-500">Formato de texto</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button class="font-bold">B</Button>
                      <Button class="italic">I</Button>
                      <Button class="underline">U</Button>
                      <Button class="line-through">S</Button>
                    </Button.Group>
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:format-bold" />
                      <Button icon="material-symbols-light:format-italic" />
                      <Button icon="material-symbols-light:format-underlined" />
                      <Button icon="material-symbols-light:strikethrough-s" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-success-500">Alineación de listas</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:format-list-bulleted" />
                      <Button icon="material-symbols-light:format-list-numbered" />
                    </Button.Group>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Botones de Navegación */}
          <Card>
            <Card.Header
              title="Botones de Navegación"
              subtitle="Patrones de navegación, contadores y acciones en grupo."
              icon={<Icon icon="material-symbols-light:swap-horiz" class="text-blue-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8">
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Paginación</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:first-page" />
                      <Button icon="material-symbols-light:chevron-left" />
                      <Button>3 / 8</Button>
                      <Button icon="material-symbols-light:chevron-right" />
                      <Button icon="material-symbols-light:last-page" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Contador</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined">
                      <Button icon="material-symbols-light:remove" />
                      <Button>1</Button>
                      <Button icon="material-symbols-light:add" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Anterior / Siguiente</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined">
                      <Button startIcon="material-symbols-light:arrow-back">Previous</Button>
                      <Button endIcon="material-symbols-light:arrow-forward">Next</Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Flechas de dirección</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:arrow-back" />
                      <Button icon="material-symbols-light:arrow-forward" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Pasos de publicación</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" color="default">
                      <Button>Draft</Button>
                      <Button>Preview</Button>
                      <Button color="success">Publish</Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Acciones peligrosas</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" color="danger">
                      <Button>Delete</Button>
                      <Button>Block</Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Acciones de edición</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" color="default">
                      <Button startIcon="material-symbols-light:visibility-outline">Preview</Button>
                      <Button startIcon="material-symbols-light:code">Code</Button>
                      <Button startIcon="material-symbols-light:edit-outline">Edit</Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Portapapeles</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm" color="default">
                      <Button>Copy</Button>
                      <Button>Cut</Button>
                      <Button>Paste</Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Guardar / Publicar</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined">
                      <Button startIcon="material-symbols-light:save-outline">Save</Button>
                      <Button color="success">Publish</Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Reproducción</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:skip-previous" />
                      <Button icon="material-symbols-light:play-arrow" />
                      <Button icon="material-symbols-light:skip-next" />
                      <Button icon="material-symbols-light:repeat" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Zoom</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:zoom-out" />
                      <Button icon="material-symbols-light:zoom-in" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Valoraciones</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:star" class="text-amber-400" />
                      <Button icon="material-symbols-light:star" class="text-amber-400" />
                      <Button icon="material-symbols-light:star" class="text-amber-400" />
                      <Button icon="material-symbols-light:star" class="text-foreground-subtle" />
                      <Button icon="material-symbols-light:star" class="text-foreground-subtle" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Me gusta / No me gusta</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button startIcon="material-symbols-light:thumb-up">24</Button>
                      <Button icon="material-symbols-light:thumb-down" />
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Región</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" color="default">
                      <div class="flex items-center bg-surface px-3 gap-2">
                        <Icon icon="material-symbols-light:language" size={16} />
                        <span class="text-sm whitespace-nowrap">Region</span>
                      </div>
                      <Button endIcon="material-symbols-light:expand-more" class="px-4">
                        us
                      </Button>
                    </Button.Group>
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest mb-6 px-1 border-l-2 border-blue-500">Iconos agrupados</h4>
                  <div class="flex flex-wrap gap-4 justify-center">
                    <Button.Group variant="outlined" size="sm">
                      <Button icon="material-symbols-light:content-copy" />
                      <Button icon="material-symbols-light:share" />
                      <Button icon="material-symbols-light:archive" />
                      <Button icon="material-symbols-light:delete" />
                    </Button.Group>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades disponibles del componente Button." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Default</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">variant</td>
                      <td class="px-6 py-4 text-foreground-subtle">contained | outlined | soft | text</td>
                      <td class="px-6 py-4 font-mono">'contained'</td>
                      <td class="px-6 py-4 text-foreground-muted">Estilo visual del botón.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">color</td>
                      <td class="px-6 py-4 text-foreground-subtle font-mono italic">ButtonColorType</td>
                      <td class="px-6 py-4 font-mono">'primary'</td>
                      <td class="px-6 py-4 text-foreground-muted">Esquema de color semántico.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">size</td>
                      <td class="px-6 py-4 text-foreground-subtle">xs | sm | md | lg | xl</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 text-foreground-muted">Dimensiones físicas del botón.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">startIcon</td>
                      <td class="px-6 py-4 text-foreground-subtle">string | IconifyIcon</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Icono a la izquierda del texto.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">endIcon</td>
                      <td class="px-6 py-4 text-foreground-subtle">string | IconifyIcon</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Icono a la derecha del texto.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">icon</td>
                      <td class="px-6 py-4 text-foreground-subtle">string | IconifyIcon</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Se usa para botones que solo contienen un icono.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">loading</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 text-foreground-muted">Muestra un spinner de carga y deshabilita el botón.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">pill</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 text-foreground-muted">Aplica bordes totalmente redondeados.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}
